import { useEffect, useState } from "react";

/**
 * Positions a specific FooterDrawer just above the on-screen keyboard.
 *
 * FooterDrawer is `position: fixed; bottom: 0`, so when the keyboard opens the
 * layout viewport doesn't change and the drawer (textarea + Done) ends up
 * behind the keyboard.
 *
 * These drawers are used WITHOUT a fixed `height` prop, so the drawer is
 * content-sized (FooterDrawer falls back to `max-height: 85vh`). This hook
 * then drives only the **wrapper** (`FooterDrawer`'s fixed
 * `position: fixed; bottom: 0` element):
 *   - lifts it by exactly the keyboard height so the sheet's bottom rests on
 *     the keyboard top (input visible, no manual scrolling to see it), and
 *   - caps the wrapper to the visible viewport (`max-height` + `overflow`) so
 *     the header can never be pushed off the top — taller content then
 *     scrolls inside FooterDrawer's own scroll area.
 *
 * Because the drawer is content-sized, short content (e.g. a single comment
 * textarea) stays a compact sheet with backdrop visible above it — it is
 * never shoved all the way up the screen. Only content taller than the
 * visible band fills it (and scrolls), which is unavoidable.
 *
 * The shared FooterDrawer is intentionally left untouched. All imperative
 * styling targets the wrapper, which has no React-managed inline `style`
 * prop, so nothing is reconciled away on FooterDrawer re-renders.
 *
 * @param open             whether the drawer is open
 * @param drawerClassName  class passed as FooterDrawer's `className` prop
 *                         (lands on the inner drawer element)
 * @returns                whether the on-screen keyboard is currently open
 */
export function useDrawerKeyboardFit(
  open: boolean,
  drawerClassName: string,
): boolean {
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const vv = window.visualViewport;
    if (!vv) return;

    const wrapperEl = () => {
      const d = document.querySelector<HTMLElement>(`.${drawerClassName}`);
      return (d?.parentElement as HTMLElement) || null;
    };

    const apply = () => {
      const keyboardInset = Math.max(
        0,
        window.innerHeight - vv.height - vv.offsetTop,
      );
      // ~150px threshold filters out URL-bar collapse / safe-area noise.
      const isOpen = keyboardInset > 150;
      setKeyboardOpen(isOpen);

      const w = wrapperEl();
      if (!w) return;
      if (isOpen) {
        // Sit the sheet on top of the keyboard, content-sized, but never
        // taller than the visible band (header stays on screen).
        w.style.bottom = `${keyboardInset}px`;
        w.style.maxHeight = `${vv.height}px`;
        w.style.overflow = "hidden";
      } else {
        w.style.bottom = "";
        w.style.maxHeight = "";
        w.style.overflow = "";
      }
    };

    // Drawer mounts via a portal/animation; re-apply next frame too. The
    // visualViewport `resize` on keyboard open also re-runs apply, so this
    // self-heals if the element isn't in the DOM on first run.
    const raf = requestAnimationFrame(apply);
    apply();
    vv.addEventListener("resize", apply);
    vv.addEventListener("scroll", apply);

    return () => {
      cancelAnimationFrame(raf);
      vv.removeEventListener("resize", apply);
      vv.removeEventListener("scroll", apply);
      const w = wrapperEl();
      if (w) {
        w.style.bottom = "";
        w.style.maxHeight = "";
        w.style.overflow = "";
      }
      setKeyboardOpen(false);
    };
  }, [open, drawerClassName]);

  return keyboardOpen;
}
